import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { calculateMonthlyContributionWithFee, calculateMonthlyContributionWithoutFee } from "Charts/Utils/CommonChartUtils";
import { type CalculateMonthlyContributionParams, calculateMonthlyContribution } from "Charts/Utils/FinancialReserveChartUtils";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { DatePicker } from "Forms/Components/DatePicker";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormSlider } from "Forms/Components/FormSlider";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { FormSwitch } from "Forms/Components/FormSwitch";
import { type FinancialReserveGoalForm, financialReserveFormSchema, minDate } from "Forms/Schemas/Goals/financialReserveFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetFinancialReserveGoalQueryKey,
  useCreateFinancialReserveGoal,
  useUpdateFinancialReserveGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type FinancialReserveGoalDto, FinancialReserveGoalDtoPriorityLevel } from "Infrastructure/ApiZfp/Schema";
import { DEFAULT_FEE_RATE, DEFAULT_INFLATION_RATE, INTEREST_RATE_SAVINGS } from "Infrastructure/Constants/financialConstants";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { addMonths, differenceInMonths, format, isBefore, startOfDay } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { FinancialReserveChart } from "./FinancialReserveChart";
import { ModelingAccordion } from "./ModelingAccordion";
import { ModelingHint } from "./ModelingHint";

type Props = {
  readonly onClose: () => void;
  readonly editGoal?: FinancialReserveGoalDto;
  readonly goalId?: string;
};

export const FinancialReserveForm = forwardRef<{}, Props>(({ onClose, editGoal, goalId }, ref) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const defaultValues = useMemo(() => {
    if (editGoal) {
      const withFee = Number.isFinite(editGoal.modeling.investmentAmount?.value) ? editGoal.modeling.investmentAmount?.value : 0;
      const fee = Number.isFinite(editGoal.modeling.fee?.rate) ? editGoal.modeling.fee?.rate : 0;
      const withoutFee = calculateMonthlyContributionWithoutFee(withFee as number, fee as number);

      return {
        name: editGoal.name,
        includeFee: editGoal.modeling.fee?.enabled,
        includeInflation: editGoal.modeling.inflation?.enabled,
        inflationRate: Number.isFinite(editGoal.modeling.inflation?.rate) ? (editGoal.modeling.inflation?.rate as any) : 0,
        feeRate: Number.isFinite(editGoal.modeling.fee?.rate) ? (editGoal.modeling.fee?.rate as any) : 0,
        interestRate: Number.isFinite(editGoal.modeling.appreciationRate) ? (editGoal.modeling.appreciationRate as any) : 0,
        currentAmount: Number.isFinite(editGoal.data.currentFinancialReserve?.value)
          ? (editGoal.data.currentFinancialReserve?.value as any)
          : 0,
        targetAmount: Number.isFinite(editGoal.data.requiredFinancialReserve?.value)
          ? (editGoal.data.requiredFinancialReserve?.value as any)
          : 0,
        targetDate: new Date(editGoal.data.targetDate as string),
        monthlyContribution: Number.isFinite(withoutFee) ? (withoutFee as any) : 0,
        monthlyContributionWithFee: Number.isFinite(withFee) ? (withFee as any) : 0,
        note: editGoal.note || "",
        priority: editGoal.priorityLevel === FinancialReserveGoalDtoPriorityLevel.high,
        referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
      };
    }

    return {
      name: t(Resources.AnalysisDetail.Goals.GoalType.FinancialReserve),
      includeFee: true,
      includeInflation: true,
      feeRate: DEFAULT_FEE_RATE,
      inflationRate: DEFAULT_INFLATION_RATE,
      interestRate: INTEREST_RATE_SAVINGS,
      currentAmount: 0,
      targetAmount: 0,
      targetDate: addMonths(new Date(), 12),
      monthlyContribution: 0,
      monthlyContributionWithFee: 0,
      referenceStartDate: new Date(),
    };
  }, [editGoal, t]);

  const methods = useForm<FinancialReserveGoalForm>({
    resolver: yupResolver(financialReserveFormSchema(t)),
    mode: "onBlur",
    defaultValues,
  });

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = methods;

  const currentAmount = watch("currentAmount");
  const targetAmount = watch("targetAmount");
  const targetDate = watch("targetDate");
  const monthlyContribution = watch("monthlyContribution");
  const monthlyContributionWithFee = watch("monthlyContributionWithFee");
  const includeInflation = watch("includeInflation");
  const inflationRate = watch("inflationRate");
  const interestRate = watch("interestRate");
  const includeFee = watch("includeFee");
  const feeRate = watch("feeRate");
  const referenceStartDate = watch("referenceStartDate");

  const isInvalidTargetDate = isBefore(startOfDay(targetDate), startOfDay(minDate));

  const shouldRenderChart =
    targetDate &&
    !isInvalidTargetDate &&
    monthlyContribution > 0 &&
    targetAmount > 0 &&
    Number.isFinite(Number(monthlyContribution)) &&
    Number.isFinite(Number(targetAmount)) &&
    Number.isFinite(Number(currentAmount)) &&
    Number.isFinite(Number(interestRate)) &&
    Number.isFinite(Number(inflationRate));

  const { mutate: createFinancialReserveGoal } = useCreateFinancialReserveGoal();

  const { mutate: updateFinancialReserveGoal } = useUpdateFinancialReserveGoal();

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (editGoal) {
        handleSubmit(onEditGoal)();
      } else {
        handleSubmit(onCreateGoal)();
      }
    },
  }));

  const onChangeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isEditingTargetAmountRef = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      if (onChangeTimeoutRef.current) {
        clearTimeout(onChangeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!targetDate || isInvalidTargetDate) return;
    clearErrors("targetDate");
    onChangeTargetDate();
  }, [isInvalidTargetDate, targetDate, clearErrors]);

  const onChangeTargetDate = () => {
    onChange({
      currentAmount: parseNumber(currentAmount),
      durationInMonths: targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1,
      targetAmount: parseNumber(targetAmount),
      inflationRate,
      interestRate,
    });
  };

  const onChange = ({
    currentAmount,
    inflationRate,
    interestRate,
    targetAmount,
    durationInMonths,
  }: CalculateMonthlyContributionParams & { currentAmount: number }) => {
    if (onChangeTimeoutRef.current) {
      clearTimeout(onChangeTimeoutRef.current);
    }

    onChangeTimeoutRef.current = setTimeout(() => {
      if (interestRate === undefined || targetAmount === undefined || durationInMonths === undefined) {
        return;
      }

      if (durationInMonths <= 0 || targetAmount < 0 || currentAmount < 0) {
        return;
      }

      if (interestRate !== undefined && targetAmount !== undefined) {
        const monthlyContribution = calculateMonthlyContribution({
          currentAmount: Number(currentAmount) || 0,
          targetAmount: Number(targetAmount) || 0,
          durationInMonths: Number(durationInMonths || 1),
          interestRate: Number(interestRate) || 0,
          inflationRate: Number(inflationRate) || 0,
        });

        if (Number.isFinite(monthlyContribution) && !Number.isNaN(monthlyContribution)) {
          setValue("monthlyContribution", monthlyContribution as any);

          const newContributionWithFee = calculateMonthlyContributionWithFee(monthlyContribution, parseNumber(feeRate) || 0);
          if (Number.isFinite(newContributionWithFee) && !Number.isNaN(newContributionWithFee)) {
            setValue("monthlyContributionWithFee", newContributionWithFee as any);
          }
        }
      }
    }, 100);
  };

  const parseNumber = (value: unknown): number => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === "string") {
      const normalized = value.replace(/\s/g, "").replace(/,/g, ".");
      const n = Number(normalized);
      return Number.isFinite(n) && !Number.isNaN(n) ? n : 0;
    }
    return 0;
  };

  const onMonthlyContributionChange = (newContributionWithFee?: unknown) => {
    const contribution = newContributionWithFee || monthlyContributionWithFee;
    const monthlyContributionWithoutFee = calculateMonthlyContributionWithoutFee(parseNumber(contribution) || 0, parseNumber(feeRate) || 0);

    if (Number.isFinite(monthlyContributionWithoutFee) && !Number.isNaN(monthlyContributionWithoutFee)) {
      setValue("monthlyContribution", monthlyContributionWithoutFee as any);

      const newContributionWithFeeNumber = parseNumber(contribution as any) || 0;
      if (Number.isFinite(newContributionWithFeeNumber) && !Number.isNaN(newContributionWithFeeNumber)) {
        setValue("monthlyContributionWithFee", newContributionWithFeeNumber as any);
      }

      // Recalculate targetAmount based on the updated monthly contribution
      const months = targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1;
      const inflationUsed = includeInflation ? parseNumber(inflationRate) || 0 : 0;
      const monthlyCompoundRate = (parseNumber(interestRate) - Number(inflationUsed)) / 100 / 12;
      const startAmount = parseNumber(currentAmount) || 0;

      let recalculatedTargetAmount: number;
      if (Math.abs(monthlyCompoundRate) < 1e-10) {
        recalculatedTargetAmount = startAmount + Number(monthlyContributionWithoutFee) * months;
      } else {
        const pow = (1 + monthlyCompoundRate) ** months;
        recalculatedTargetAmount = startAmount * pow + Number(monthlyContributionWithoutFee) * ((-1 + pow) / monthlyCompoundRate);
      }

      if (Number.isFinite(recalculatedTargetAmount) && !Number.isNaN(recalculatedTargetAmount)) {
        if (!isEditingTargetAmountRef.current) {
          setValue("targetAmount", Math.max(0, Math.round(recalculatedTargetAmount)) as any);
        }
      }
    }
  };

  const onCreateGoal = (data: FinancialReserveGoalForm) => {
    if (onChangeTimeoutRef.current) {
      clearTimeout(onChangeTimeoutRef.current);
    }

    const withFeeContribution = parseNumber(data.monthlyContributionWithFee);

    createFinancialReserveGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? FinancialReserveGoalDtoPriorityLevel.high : FinancialReserveGoalDtoPriorityLevel.low,
          data: {
            currentFinancialReserve: {
              value: parseNumber(data.currentAmount),
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            requiredFinancialReserve: {
              value: parseNumber(data.targetAmount),
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            targetDate: format(data.targetDate, "yyyy-MM-dd"),
          },
          modeling: {
            investmentAmount: {
              value: Number.isFinite(Number(withFeeContribution)) ? Number(withFeeContribution) : 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            inflation: {
              enabled: !!data.includeInflation,
              rate: parseNumber(data.inflationRate),
            },
            fee: {
              enabled: !!data.includeFee,
              rate: parseNumber(data.feeRate),
            },
            appreciationRate: parseNumber(data.interestRate),
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEditGoal = (data: FinancialReserveGoalForm) => {
    if (onChangeTimeoutRef.current) {
      clearTimeout(onChangeTimeoutRef.current);
    }

    const withFeeContribution = parseNumber(data.monthlyContributionWithFee);

    updateFinancialReserveGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? FinancialReserveGoalDtoPriorityLevel.high : FinancialReserveGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.targetDate, "yyyy-MM-dd"),
            currentFinancialReserve: {
              value: parseNumber(data.currentAmount),
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            requiredFinancialReserve: {
              value: parseNumber(data.targetAmount),
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            inflation: {
              enabled: !!data.includeInflation,
              rate: parseNumber(data.inflationRate),
            },
            investmentAmount: {
              value: Number.isFinite(Number(withFeeContribution)) ? Number(withFeeContribution) : 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            fee: {
              enabled: !!data.includeFee,
              rate: parseNumber(data.feeRate),
            },
            appreciationRate: parseNumber(data.interestRate),
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetFinancialReserveGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.FinancialReserveLong)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <BlCheckboxInput
                  disabled={readOnly}
                  control={control}
                  name="priority"
                  text={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  errors={errors}
                />
              </Grid>
              <Grid size={12}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  errors={errors}
                  labelEndAdornment={<StyledLabelAdornment />}
                />
              </Grid>
              <Grid size={4}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="currentAmount"
                  label={t(Resources.AnalysisDetail.Goals.CurrentEmergencyFundAmount)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      min: 0,
                      scale: 0,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                  onBlur={() =>
                    onChange({
                      currentAmount: parseNumber(currentAmount),
                      durationInMonths: targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1,
                      targetAmount,
                      inflationRate,
                      interestRate,
                    })
                  }
                />
              </Grid>
              <Grid size={4}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="targetAmount"
                  label={t(Resources.AnalysisDetail.Goals.TargetEmergencyFundAmount)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 0,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                  onFocus={() => {
                    isEditingTargetAmountRef.current = true;
                  }}
                  onBlur={() => {
                    isEditingTargetAmountRef.current = false;
                    onChange({
                      currentAmount: parseNumber(currentAmount),
                      durationInMonths: targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1,
                      targetAmount: parseNumber(targetAmount),
                      inflationRate,
                      interestRate,
                    });
                  }}
                />
              </Grid>
              <Grid size={4}>
                <DatePicker
                  control={control}
                  disabled={readOnly}
                  name="targetDate"
                  label={t(Resources.AnalysisDetail.Goals.PurchaseDate)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  minDate={minDate}
                  errors={errors}
                />
              </Grid>
              <Grid size={12}>
                <ModelingHint title={t(Resources.AnalysisDetail.Goals.RecommendedEmergencyFundAmount)}>
                  {t(Resources.AnalysisDetail.Goals.RecommendedEmergencyFundAmountHint)}
                </ModelingHint>
              </Grid>
            </Grid>
          </Box>
          {targetDate && targetAmount > 0 && (
            <Box>
              <ModelingAccordion>
                <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                  <Grid size={12}>
                    <FormSlider
                      control={control}
                      disabled={readOnly}
                      name="monthlyContributionWithFee"
                      min={0}
                      max={50_000}
                      onChange={(value) => onMonthlyContributionChange(value.toString())}
                    />
                  </Grid>
                  <Grid size={8}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="monthlyContributionWithFee"
                      label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 2,
                          radix: ",",
                          mapToRadix: ["."],
                          min: 0,
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}
                        </Typography>
                      }
                      onBlur={() => onMonthlyContributionChange()}
                    />
                  </Grid>
                  <Grid size={4}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="interestRate"
                      label={t(Resources.AnalysisDetail.Goals.InterestRate)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      onChange={(newInterestRate) =>
                        onChange({
                          durationInMonths: Math.max(1, differenceInMonths(targetDate, referenceStartDate)),
                          targetAmount: Number(targetAmount),
                          inflationRate,
                          interestRate: Number(newInterestRate),
                          currentAmount,
                        })
                      }
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          scale: 1,
                          radix: ",",
                          mapToRadix: ["."],
                          min: 0,
                        },
                      ]}
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      disabled={readOnly}
                      name="includeInflation"
                      label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                      onChange={(checked) => {
                        const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;

                        if (newInflationRate === Number(interestRate)) {
                          setValue("inflationRate", (newInflationRate + 0.1) as any);
                        } else {
                          setValue("inflationRate", newInflationRate as any);
                        }

                        onChange({
                          durationInMonths: targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1,
                          targetAmount: Number(targetAmount),
                          inflationRate: newInflationRate,
                          interestRate,
                          currentAmount,
                        });
                      }}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={!includeInflation || readOnly}
                      control={control}
                      name="inflationRate"
                      errors={errors}
                      onChange={(newInflationRate) => {
                        onChange({
                          durationInMonths: targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1,
                          targetAmount: Number(targetAmount),
                          inflationRate: includeInflation ? Number(newInflationRate) : 0,
                          interestRate,
                          currentAmount,
                        });
                      }}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          radix: ",",
                          mapToRadix: ["."],
                          min: 0,
                          max: 20,
                          scale: 1,
                        },
                      ]}
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      disabled={readOnly}
                      name="includeFee"
                      label={t(Resources.AnalysisDetail.Goals.IncludeFee)}
                      errors={errors}
                      onChange={(checked) => {
                        const newFeeRate = checked ? DEFAULT_FEE_RATE : 0;
                        const currentMonthlyContribution = parseNumber(monthlyContribution) || 0;
                        const newContributionWithFee = calculateMonthlyContributionWithFee(currentMonthlyContribution, newFeeRate);

                        setValue("feeRate", newFeeRate as any);

                        if (Number.isFinite(newContributionWithFee) && !Number.isNaN(newContributionWithFee)) {
                          setValue("monthlyContributionWithFee", newContributionWithFee as any);

                          // Recalculate targetAmount based on the updated monthly contribution
                          const months = targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1;
                          const inflationUsed = includeInflation ? parseNumber(inflationRate) || 0 : 0;
                          const monthlyCompoundRate = (parseNumber(interestRate) - Number(inflationUsed)) / 100 / 12;
                          const startAmount = parseNumber(currentAmount) || 0;

                          let recalculatedTargetAmount: number;
                          if (Math.abs(monthlyCompoundRate) < 1e-10) {
                            recalculatedTargetAmount = startAmount + Number(currentMonthlyContribution) * months;
                          } else {
                            const pow = (1 + monthlyCompoundRate) ** months;
                            recalculatedTargetAmount =
                              startAmount * pow + Number(currentMonthlyContribution) * ((-1 + pow) / monthlyCompoundRate);
                          }

                          if (Number.isFinite(recalculatedTargetAmount) && !Number.isNaN(recalculatedTargetAmount)) {
                            if (!isEditingTargetAmountRef.current) {
                              setValue("targetAmount", Math.max(0, Math.round(recalculatedTargetAmount)) as any);
                            }
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={!includeFee || readOnly}
                      control={control}
                      name="feeRate"
                      errors={errors}
                      onChange={(newFeeRate) => {
                        const currentMonthlyContribution = parseNumber(monthlyContribution) || 0;
                        const newFeeRateNumber = parseNumber(newFeeRate as any) || 0;
                        const newContributionWithFee = calculateMonthlyContributionWithFee(currentMonthlyContribution, newFeeRateNumber);

                        if (Number.isFinite(newContributionWithFee) && !Number.isNaN(newContributionWithFee)) {
                          setValue("monthlyContributionWithFee", newContributionWithFee as any);

                          // Recalculate targetAmount based on the updated monthly contribution
                          const months = targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1;
                          const inflationUsed = includeInflation ? parseNumber(inflationRate) || 0 : 0;
                          const monthlyCompoundRate = (parseNumber(interestRate) - Number(inflationUsed)) / 100 / 12;
                          const startAmount = parseNumber(currentAmount) || 0;

                          let recalculatedTargetAmount: number;
                          if (Math.abs(monthlyCompoundRate) < 1e-10) {
                            recalculatedTargetAmount = startAmount + Number(currentMonthlyContribution) * months;
                          } else {
                            const pow = (1 + monthlyCompoundRate) ** months;
                            recalculatedTargetAmount =
                              startAmount * pow + Number(currentMonthlyContribution) * ((-1 + pow) / monthlyCompoundRate);
                          }

                          if (Number.isFinite(recalculatedTargetAmount) && !Number.isNaN(recalculatedTargetAmount)) {
                            if (!isEditingTargetAmountRef.current) {
                              setValue("targetAmount", Math.max(0, Math.round(recalculatedTargetAmount)) as any);
                            }
                          }
                        }
                      }}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          scale: 1,
                          radix: ",",
                          mapToRadix: ["."],
                          min: 0,
                          max: 20,
                        },
                      ]}
                    />
                  </Grid>
                </Grid>
              </ModelingAccordion>
            </Box>
          )}
          <Box>
            <Grid container spacing={Spacing.XL}>
              {shouldRenderChart && (
                <Grid size={12}>
                  <Box padding={Spacing.S} display="flex" justifyContent="space-around" alignItems="center">
                    <Stack spacing={Spacing.XS} display="flex" justifyContent="center" alignItems="center">
                      <Typography variant="caption" fontWeight={500}>
                        {t(Resources.AnalysisDetail.Goals.EmergencyFundCreation)}
                      </Typography>
                      <Typography variant="caption">
                        {formatCurrency(Math.round(Number(monthlyContributionWithFee)), CurrencyCode.CZK)}/{t(Resources.Common.Month)}
                      </Typography>
                    </Stack>
                    <Stack spacing={Spacing.XS} display="flex" justifyContent="center" alignItems="center">
                      <Typography variant="caption" fontWeight={500}>
                        {t(Resources.AnalysisDetail.Goals.EmergencyFundAppreciation)}
                      </Typography>
                      <Typography variant="caption" color="transparent">
                        /
                      </Typography>
                    </Stack>
                  </Box>
                  <FinancialReserveChart
                    currentAmount={Number.isFinite(Number(currentAmount)) ? Number(currentAmount) : 0}
                    targetAmount={Number.isFinite(Number(targetAmount)) ? Number(targetAmount) : 0}
                    targetDate={targetDate}
                    referenceStartDate={referenceStartDate}
                    monthlyContributionAmount={Number.isFinite(Number(monthlyContribution)) ? Number(monthlyContribution) : 0}
                    inflationRate={includeInflation && Number.isFinite(Number(inflationRate)) ? Number(inflationRate) : 0}
                    interestRate={Number.isFinite(Number(interestRate)) ? Number(interestRate) : 0}
                  />
                </Grid>
              )}
              <Grid size={12}>
                <BlFormInput
                  disabled={readOnly}
                  control={control}
                  label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                  name="note"
                  errors={errors}
                  multiline
                  minRows={6}
                  maxRows={8}
                  characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
